<template>
  <div class="list-bills">
    <PageHeader title="Bills" :action="{ title: 'Add new bill', to: { name: 'CreateBill' } }"/>

    <div class="card">
      <BaseTable
        :items="bills ?? []"
        :fields="tableFields"
        :is-loading="isPending"
        :error="error"
        :is-searchable="true"
        :paginate="true"
        :next-page="nextPage"
        :prev-page="prevPage"
        :page="currentPage"
        :total-records="totalRecords"
        :total-pages="totalPages"
        :number-per-page="numberPerPage"
        @on-search="(val) => search(val)"
      >
        <template #billNumber-data="{ row }">
          <router-link v-if="row.id" :to="{ name: 'ShowBill', params: { id: row.id } }">
            {{ row.billNumber || row.id }}
          </router-link>
          <span v-else>N/A</span>
        </template>

        <template #status-data="{ row }">
          <BaseBadge
            :variant="row.status === BILL_STATUS.PAID ? 'success' : row.status === BILL_STATUS.OVERDUE ? 'error' : 'warning'"
            class="capitalize"
          >
            {{ row.status }}
          </BaseBadge>
        </template>

        <template #dueDate-data="{ row }">
          <span>{{ formatDate(row.dueDate) }}</span>
        </template>

      <template #projectId-data="{ row }">
        <router-link v-if="projects && projects?.find(project => project.id === row.projectId)?.id" :to="{ name: 'ShowProject', params: { projectId: row.projectId, customerId: projects?.find(project => project.id === row.projectId)?.customerId } }">
          {{ projects?.find(project => project.id === row.projectId)?.name || row.projectId }}
        </router-link>
        <span v-else>
          {{ row.projectId }}
        </span>
      </template>

        <template #invoice-data="{ row }">
          <span v-if="row.workOrderId">{{ row.workOrderId }}</span>
          <span v-else-if="row.purchaseOrderId">{{ row.purchaseOrderId }}</span>
        </template>

        <template #payee-data="{ row }">
          <span v-if="row.workOrderId && row.payee">
            <RouterLink :to="{ name: 'ShowSubcontractor', params: { id: row.payee } }">
              {{ subcontractors?.find(subcontractor => subcontractor.id === row.payee)?.name || row.payee }}
            </RouterLink>
          </span>
          <span v-else-if="row.purchaseOrderId && row.payee">
            <RouterLink :to="{ name: 'ShowVendor', params: { id: row.payee } }">
              {{ vendors?.find(vendor => vendor.id === row.payee)?.name || row.payee }}
            </RouterLink>
          </span>
          <span v-else>
            {{ row.payee }}
          </span>
        </template>

        <template #total-data="{ row }">
          {{ currency(row.total) }}
        </template>
      </BaseTable>
      </div>

    <DeleteConfirmationModal
      id="deleteConfirmationModal"
      v-model="showDeleteConfirmationModal"
      title="Delete Bill"
      message="Are you sure you want to delete this bill?"
      @delete="deleteBill"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import DeleteConfirmationModal from '@/components/app/DeleteConfirmationModal.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import { useHead } from '@unhead/vue'
import { useQuery, useMutation } from '@tanstack/vue-query'
import BillService from '@/services/BillService'
import { useNotification } from '@kyvg/vue3-notification'
import { Bill, BILL_STATUS } from '@/models/bill'
import ProjectService from '@/services/ProjectService'
import { inArray } from '@/utils/queryFilters'
import { formatDate, currency } from '@/utils'
import useGetAllvendors from '@/composables/useGetAllVendors'
import useGetAllSubs from '@/composables/useGetAllSubs'
import { useSearchablePagination } from '@/composables/useSearchablePagination'

useHead({ title: 'Bills' })

const tableFields = [
  { key: 'billNumber', label: 'Bill #', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'dueDate', label: 'Due Date', sortable: true },
  { key: 'invoice', label: 'WO# / PO#', sortable: true },
  { key: 'projectId', label: 'Project', sortable: true },
  { key: 'payee', label: 'Vendor / Sub', sortable: true },
  { key: 'total', label: 'Total', sortable: true },
]

const numberPerPage = ref(25)
const billService = new BillService()

const {
  results: bills,
  isPending,
  error,
  search,
  currentPage,
  totalPages,
  totalRecords,
  nextPage,
  prevPage,
} = useSearchablePagination<Bill>({
  indexName: 'bills',
  service: billService,
  numPerPage: numberPerPage.value,
  defaultOrderBy: 'createdAt',
  defaultOrderDir: 'desc'
})

const { notify } = useNotification()
const showDeleteConfirmationModal = ref(false)

const { mutate: deleteBill } = useMutation({
  mutationFn: async (bill: Bill) => {
    await billService.delete(bill.id)
  },
  onSuccess: () => {
    showDeleteConfirmationModal.value = false
    notify({ type: 'success', text: 'Bill deleted successfully' })
  },
  onError: (error) => {
    console.error(error)
    notify({ type: 'danger', text: 'Failed to delete bill' })
  },
})

const uniqueProjects = computed(() => Array.from(new Set(bills.value?.map((bill: Bill) => bill.projectId ?? ''))))
const projectService = new ProjectService()
const { data: projects } = useQuery({
  queryKey: ['projects', uniqueProjects, currentPage],
  queryFn: () => projectService.where([inArray('id', uniqueProjects.value as string[])], [])
})

const { data: subcontractors } = useGetAllSubs()
const { data: vendors } = useGetAllvendors()

</script>
